import { getGLBData } from '@proxies/getGLBData'
import { pickLocalizedText } from '@utils/i18n/uiLocale'
import {
  isCatalog166Series,
  rinconeroBaseCatalogCode,
} from '@utils/sceneObjects/items/rinconeroUtils'

export const COMFORT_DEPTH_CM = {
  regular: 95,
  comfort: 100,
  plus: 105,
  sleep: 150,
} as const

export type ComfortOption = keyof typeof COMFORT_DEPTH_CM

export type ComfortOptionDefinition = {
  id: ComfortOption
  label: string
  depthCm: number
}

export const COMFORT_OPTIONS: ComfortOptionDefinition[] = [
  { id: 'regular', label: '92 cm', depthCm: 95 },
  { id: 'comfort', label: '97 cm', depthCm: 100 },
  { id: 'plus', label: '102 cm', depthCm: 105 },
  { id: 'sleep', label: '150 cm', depthCm: 150 },
]

export function comfortOptionUnavailableMessage(optionLabel: string): string {
  return pickLocalizedText({
    es: `Esta unidad no tiene la opción de confort ${optionLabel} para esa unidad.`,
    en: `This unit does not have the ${optionLabel} comfort option.`,
  })
}

const DEPTH_VALUES = new Set<number>(Object.values(COMFORT_DEPTH_CM))

export function isArmCatalog(catalogID: unknown): boolean {
  return /^BR\d+(-\d+)?$/i.test(String(catalogID || '').trim())
}

function stripHandSuffix(catalog: string): { base: string; handSuffix: string } {
  const match = catalog.match(/^(.+?)(\s+[DI])$/i)
  if (match) return { base: match[1].trim(), handSuffix: match[2] }
  return { base: catalog.trim(), handSuffix: '' }
}

function normalizeCatalogBase(catalogID: unknown): { base: string; handSuffix: string } {
  const raw = String(catalogID || '').trim()
  const normalized = rinconeroBaseCatalogCode(raw) || raw
  return stripHandSuffix(normalized)
}

/** Rinconero 166 (RINCÓNMENU): 166-95-95 → ambos fondos cambian juntos (166-100-100, etc.). */
function isRinconeroDualDepthCatalog(base: string): boolean {
  if (!isCatalog166Series(base)) return false
  const parts = base.split('-').filter(Boolean)
  if (parts.length !== 3 || parts[0] !== '166') return false
  const depthA = Number(parts[1])
  const depthB = Number(String(parts[2]).replace(/i$/i, ''))
  return (
    Number.isFinite(depthA) &&
    Number.isFinite(depthB) &&
    DEPTH_VALUES.has(depthA) &&
    DEPTH_VALUES.has(depthB)
  )
}

function parseRinconeroDualDepthCm(base: string): number | null {
  if (!isRinconeroDualDepthCatalog(base)) return null
  const parts = base.split('-').filter(Boolean)
  return Number(parts[1])
}

function depthFromThicknessMm(thicknessMm: unknown): number | null {
  const mm = Number(thicknessMm)
  if (!Number.isFinite(mm) || mm <= 0) return null
  if (mm >= 1450) return 150
  if (mm >= 1010) return 105
  if (mm >= 965) return 100
  if (mm >= 900) return 95
  return null
}

export function parseCatalogDepthCm(catalogID: unknown, thicknessMm?: unknown): number | null {
  const raw = String(catalogID || '').trim()
  if (!raw || isArmCatalog(raw)) return null

  const { base } = normalizeCatalogBase(raw)
  const rinconeroDepth = parseRinconeroDualDepthCm(base)
  if (rinconeroDepth != null) return rinconeroDepth

  const parts = base.split('-').filter(Boolean)
  if (parts.length >= 2) {
    const lastPart = parts[parts.length - 1]
    const numericLast = Number(String(lastPart).replace(/i$/i, ''))
    if (Number.isFinite(numericLast) && DEPTH_VALUES.has(numericLast)) {
      return numericLast
    }
  }

  return depthFromThicknessMm(thicknessMm)
}

export function buildCatalogWithDepthCm(catalogID: unknown, targetDepthCm: number): string | null {
  if (!DEPTH_VALUES.has(targetDepthCm)) return null

  const raw = String(catalogID || '').trim()
  if (!raw || isArmCatalog(raw)) return null

  const currentDepth = parseCatalogDepthCm(raw)
  if (currentDepth == null) return null

  const { base, handSuffix } = normalizeCatalogBase(raw)
  const parts = base.split('-').filter(Boolean)
  if (parts.length < 2) return null

  if (isRinconeroDualDepthCatalog(base)) {
    parts[1] = String(targetDepthCm)
    const lastPart = parts[2]
    const hadRinconeroI = /i$/i.test(String(lastPart)) && String(lastPart).length > 1
    parts[2] = hadRinconeroI ? `${targetDepthCm}i` : String(targetDepthCm)
    return `${parts.join('-')}${handSuffix}`
  }

  const lastPart = parts[parts.length - 1]
  const hadRinconeroI = /i$/i.test(String(lastPart)) && String(lastPart).length > 1
  parts[parts.length - 1] = hadRinconeroI ? `${targetDepthCm}i` : String(targetDepthCm)

  return `${parts.join('-')}${handSuffix}`
}

export function getComfortOptionFromCatalog(
  catalogID: unknown,
  thicknessMm?: unknown,
): ComfortOption | null {
  const depth = parseCatalogDepthCm(catalogID, thicknessMm)
  if (depth == null) return null
  return COMFORT_OPTIONS.find((option) => option.depthCm === depth)?.id ?? null
}

export function isComfortVariantEligible(catalogID: unknown, thicknessMm?: unknown): boolean {
  return parseCatalogDepthCm(catalogID, thicknessMm) != null
}

export async function comfortVariantExistsInCatalog(catalogID: string): Promise<boolean> {
  const data = await getGLBData(catalogID)
  if (!data) return false
  if (Array.isArray(data)) return data.length > 0

  const referenciaBlender = String((data as { referenciaBlender?: unknown }).referenciaBlender || '').trim()
  if (!referenciaBlender) return false

  return true
}
